import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { CreationMode } from '@components/storage-accounts/add-storage-account-wizard/add-storage-account-wizard.constants';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AbilityService } from 'ability';
import { I18NextPipe } from 'angular-i18next';
import {
  ButtonComponent,
  CardGroupComponent,
  ClipboardComponent,
  FormGroupComponent,
  InputComponent,
  InputLabelComponent,
  InputPasswordComponent,
  MbsSize,
  ModalService,
  SafePipe,
  SelectComponent
} from 'mbs-ui-kit';
import { PermissionsEnum, StorageType } from 'mbs-ui-shared';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { HelpService } from 'mbs-ui-shared@help/help.service';
import { HelpLinkDirective } from 'mbs-ui-shared@help/helpLink.directive';
import { S3AuthType as AuthType } from 'mbs-ui-shared@models/StorageAccount';
import { e2eLocators, fakeSecretKey } from '../credential-forms.constants';
import { WasabiCredentialForm } from '../credential-forms.model';
import { MinimumRequiredPermissionsModalComponent } from '../s3/components/minimum-required-permissions-modal/minimum-required-permissions-modal.component';
import { getAuthTypes } from '../s3/credential-form-s3.constants';

@UntilDestroy()
@Component({
  selector: 'mbs-credential-form-wasabi',
  templateUrl: './credential-form-wasabi.component.html',
  styleUrls: ['../credential-forms.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonComponent,
    CardGroupComponent,
    ClipboardComponent,
    FormGroupComponent,
    HelpLinkDirective,
    I18NextPipe,
    InputComponent,
    InputLabelComponent,
    InputPasswordComponent,
    NgTemplateOutlet,
    ReactiveFormsModule,
    SafePipe,
    SelectComponent
  ]
})
export class CredentialFormWasabiComponent implements OnInit {
  private ability = inject(AbilityService);
  private authFacade = inject(AuthFacade);
  private helpService = inject(HelpService);
  private i18n = inject(I18NextPipe);
  private modalService = inject(ModalService);

  @Input() form: WasabiCredentialForm;
  @Input() mode: CreationMode;

  public readonly StorageType = StorageType;
  public readonly PermissionEnum = PermissionsEnum;
  public readonly AuthType = AuthType;
  public readonly authTypes = getAuthTypes(this.i18n, this.helpService);
  public readonly mbsSize = MbsSize;
  public readonly fakeSecretKey = fakeSecretKey;
  public readonly e2eLocators = e2eLocators;
  public readonly createRoleHelpLink = this.helpService.getHelpLink('/billing-storage/storage-providers/wasabi/create-iam-role');
  public readonly createUserHelpLink = this.helpService.getHelpLink('/billing-storage/storage-providers/wasabi/create-iam-user');

  public get isHelpAllowed(): boolean {
    return this.ability.can('read', PermissionsEnum.HelpMarketing);
  }

  public get manualHint(): string {
    return this.isHelpAllowed
      ? 'storages:addStorageAccountWizard.inputCredentialsStep.wasabiAuthTypeManualHint'
      : 'storages:addStorageAccountWizard.inputCredentialsStep.wasabiAuthTypeManualHintWithoutHelp';
  }

  public arnRoleControl: FormControl;
  public authTypeControl: FormControl;
  public accessKeyControl: FormControl;
  public secretKeyControl: FormControl;

  ngOnInit(): void {
    this.defineControls();

    this.authTypeControl.valueChanges.pipe(untilDestroyed(this)).subscribe((type) => {
      if (type === AuthType.MBS || type === AuthType.Legacy) {
        this.arnRoleControl.clearValidators();
        this.arnRoleControl.patchValue(null);
        this.accessKeyControl.setValidators([Validators.required]);
        this.secretKeyControl.setValidators([Validators.required]);
      }

      if (type === AuthType.Manual) {
        this.arnRoleControl.setValidators([Validators.required]);
        this.accessKeyControl.clearValidators();
        this.accessKeyControl.patchValue(null);
        this.secretKeyControl.clearValidators();
        this.secretKeyControl.patchValue(null);
      }
    });

    this.accessKeyControl.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      this.secretKeyControl.patchValue('');
      this.secretKeyControl.updateValueAndValidity();
      this.secretKeyControl.markAsDirty();
      this.secretKeyControl.markAsTouched();
    });

    this.authTypeControl.patchValue(
      this.mode === CreationMode.Create ? AuthType.MBS : this.arnRoleControl.value ? AuthType.Manual : AuthType.Legacy
    );
  }

  public get id(): string {
    return this.authFacade.currentUserValue?.ProviderInfo.Id;
  }

  public get authType(): AuthType {
    return this.form.get('authType').value;
  }

  public openMinimumRequiredPermissionsModal(): void {
    this.modalService.openCustom(MinimumRequiredPermissionsModalComponent);
  }

  private defineControls(): void {
    this.arnRoleControl = this.form.get('arnRole') as FormControl;
    this.authTypeControl = this.form.get('authType') as FormControl;
    this.accessKeyControl = this.form.get('accessKey') as FormControl;
    this.secretKeyControl = this.form.get('secretKey') as FormControl;
  }
}
